// Sentence-level diff for chart section edits, used to persist human
// contributions (manual typing or voice dictation) into the canonical
// "Manual Input" / context stream so they survive mass regeneration.
//
// Returns a short, token-efficient summary string (or null if the change
// was whitespace/format-only). Output reads like:
//   Corrected: "blood sugar was high after breakfast" → "blood sugar was high after lunch"
//   Added: "Patient reports nausea since Monday."
//   Removed: "Denies fever."

const stripHtml = (s) => {
  if (!s) return "";
  return (
    String(s)
      // Convert block-level boundaries to newlines BEFORE stripping tags, so
      // structured sections (Review of Systems, Plan of Care) split per-item
      // instead of collapsing to one mega-sentence. Without this, a <ul> with
      // no period punctuation between <li>s reads as a single "sentence" and
      // single-bullet edits hit the 0.95 unchanged threshold below — yielding
      // a null summary and no Manual Input log.
      .replace(/<\/(li|p|div|h[1-6])>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
  );
};

const normalize = (s) => stripHtml(s).replace(/\s+/g, " ").trim();

const splitSentences = (s) => {
  const stripped = stripHtml(s);
  if (!stripped.trim()) return [];
  return stripped
    .split(/(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÑ¿¡])|\n+/)
    .map((x) => x.replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

// Cheap fuzzy similarity: shared whitespace-tokens / max(len(a), len(b)).
const sentenceSimilarity = (a, b) => {
  if (a === b) return 1;
  const ta = new Set(a.toLowerCase().split(/\s+/).filter(Boolean));
  const tb = new Set(b.toLowerCase().split(/\s+/).filter(Boolean));
  if (!ta.size || !tb.size) return 0;
  let shared = 0;
  ta.forEach((tok) => {
    if (tb.has(tok)) shared += 1;
  });
  return shared / Math.max(ta.size, tb.size);
};

const MAX_ENTRIES = 8;

export function diffSectionEdit(prev, next, { maxEntries = MAX_ENTRIES } = {}) {
  const prevNorm = normalize(prev);
  const nextNorm = normalize(next);
  if (!nextNorm && !prevNorm) return null;
  if (prevNorm === nextNorm) return null;

  const prevSentences = splitSentences(prev);
  const nextSentences = splitSentences(next);

  // Greedy pairing: for each prev sentence find best match in next.
  const matchedNext = new Set();
  const lines = [];

  prevSentences.forEach((p) => {
    let bestIdx = -1;
    let bestScore = 0;
    nextSentences.forEach((n, idx) => {
      if (matchedNext.has(idx)) return;
      const score = sentenceSimilarity(p, n);
      if (score > bestScore) {
        bestScore = score;
        bestIdx = idx;
      }
    });
    if (bestIdx >= 0 && bestScore >= 0.95) {
      // Unchanged
      matchedNext.add(bestIdx);
    } else if (bestIdx >= 0 && bestScore >= 0.4) {
      // Correction
      matchedNext.add(bestIdx);
      lines.push(`Corrected: "${p}" → "${nextSentences[bestIdx]}"`);
    } else {
      // Removed
      lines.push(`Removed: "${p}"`);
    }
  });

  nextSentences.forEach((n, idx) => {
    if (matchedNext.has(idx)) return;
    lines.push(`Added: "${n}"`);
  });

  if (!lines.length) return null;
  if (lines.length > maxEntries) {
    const overflow = lines.length - maxEntries;
    lines.splice(maxEntries);
    lines.push(`(+${overflow} more change${overflow === 1 ? "" : "s"})`);
  }
  return lines.join("\n");
}
